import React from "react";
import { AnimatePresence, motion } from "motion/react";
import styled, { css } from "styled-components";
import { classes } from "lib/actions/util";
import { Placement, autoUpdate, offset, flip } from "@floating-ui/dom";
import {
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  FloatingPortal,
} from "@floating-ui/react";

type Variant = "flat" | "raised" | "simple";

interface ControlProps {
  variant: Variant;
}

const Control = styled.div<ControlProps>`
  background: none;
  ${(p) =>
    p.variant === "flat" &&
    css`
      background: white;
      border: solid 1px rgb(223, 223, 223);
    `}
  ${(p) =>
    p.variant === "raised" &&
    css`
      background: white;
      box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
    `}
`;

const Dropdown = ({
  buttonContent,
  dropdownContent,
  className,
  variant = "raised",
  dropdownWidth,
  dropdownMaxHeight,
  isOpen,
  toggleOpen,
  placement = "bottom-end",
}: {
  buttonContent: React.ReactNode;
  dropdownContent: React.ReactNode;
  className?: string;
  variant?: Variant;
  dropdownWidth?: number | "auto" | "max-content";
  dropdownMaxHeight?: number | "none";
  isOpen: boolean;
  toggleOpen: () => void;
  placement?: Placement;
}) => {
  const open = isOpen;
  const setOpen = toggleOpen;

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset({ mainAxis: 8 }), flip()],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <>
      <Control
        ref={refs.setReference}
        className={classes(
          "cursor-pointer select-none overflow-hidden",
          variant !== "simple" &&
            "flex flex-row items-center justify-center rounded-md px-3 py-2",
          className,
        )}
        variant={variant}
        onClick={setOpen}
        tabIndex={0}
        {...getReferenceProps()}
      >
        {buttonContent}
      </Control>
      <AnimatePresence initial={false}>
        {open && (
          // FloatingPortal is required so that dropdowns in the side panel are not clipped by overflow-hidden
          <FloatingPortal>
            <motion.div
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                ...((dropdownWidth && { width: dropdownWidth }) || null),
                ...((typeof dropdownMaxHeight === "number" && {
                  maxHeight: dropdownMaxHeight,
                }) ||
                  null),
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              key="menu"
              className={classes(
                "select-none overflow-x-hidden text-gray-900",
                "rounded-md bg-white shadow-md dark:bg-gray-100",
                "z-50 ring-1 ring-black ring-opacity-5",
                !dropdownWidth && "w-48",
                typeof dropdownMaxHeight !== "number" &&
                  dropdownMaxHeight !== "none" &&
                  "max-h-[40vh]",
              )}
              role="menu"
              aria-orientation="vertical"
              {...getFloatingProps()}
            >
              {dropdownContent}
            </motion.div>
          </FloatingPortal>
        )}
      </AnimatePresence>
    </>
  );
};

export default Dropdown;
